import React from 'react';
import { ArrowLeft, Package, RefreshCw, Clock, AlertCircle, CheckCircle, MessageCircle } from 'lucide-react';

const ReturnPolicy = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-black text-white p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-white/10 rounded">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-2xl font-black tracking-tighter">RETURN & EXCHANGE POLICY</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-8">
        {/* Hero */}
        <div className="bg-yellow-400 border-4 border-black shadow-[8px_8px_0px_black] p-8 text-center">
          <RefreshCw size={64} className="mx-auto mb-4" />
          <h2 className="text-4xl font-black mb-2">HASSLE-FREE RETURNS</h2>
          <p className="text-xl">7-day easy return & exchange policy</p>
        </div>

        {/* Quick Info Cards */}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white border-4 border-black p-6 text-center">
            <Clock size={40} className="mx-auto mb-3 text-blue-500" />
            <h3 className="font-black text-lg">7 DAYS</h3>
            <p className="text-sm text-gray-600">Return window from delivery</p>
          </div>
          <div className="bg-white border-4 border-black p-6 text-center">
            <Package size={40} className="mx-auto mb-3 text-green-500" />
            <h3 className="font-black text-lg">FREE PICKUP</h3>
            <p className="text-sm text-gray-600">We'll collect from your door</p>
          </div>
          <div className="bg-white border-4 border-black p-6 text-center">
            <RefreshCw size={40} className="mx-auto mb-3 text-purple-500" />
            <h3 className="font-black text-lg">5-7 DAYS</h3>
            <p className="text-sm text-gray-600">Refund processing time</p>
          </div>
        </div>

        {/* Eligible for Return */}
        <div className="bg-white border-4 border-black shadow-[8px_8px_0px_black] p-6">
          <h3 className="text-2xl font-black mb-4 flex items-center gap-3">
            <CheckCircle className="text-green-500" size={28} />
            ELIGIBLE FOR RETURN
          </h3>
          <ul className="space-y-3">
            {[
              'Wrong size delivered',
              'Wrong product delivered',
              'Product received in damaged condition',
              'Manufacturing defect (stitching issues, print defects)',
              'Product significantly different from website images'
            ].map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span className="w-6 h-6 bg-green-500 text-white flex items-center justify-center flex-shrink-0 font-bold text-sm">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Not Eligible for Return */}
        <div className="bg-white border-4 border-black shadow-[8px_8px_0px_black] p-6">
          <h3 className="text-2xl font-black mb-4 flex items-center gap-3">
            <AlertCircle className="text-red-500" size={28} />
            NOT ELIGIBLE FOR RETURN
          </h3>
          <ul className="space-y-3">
            {[
              'Products used, washed, or altered',
              'Products without original tags',
              'Return request after 7 days',
              'Products damaged by customer',
              'Change of mind (unless exchanging for different size)',
              'Items purchased during flash sales (marked as non-returnable)'
            ].map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span className="w-6 h-6 bg-red-500 text-white flex items-center justify-center flex-shrink-0 font-bold text-sm">✕</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* How to Return */}
        <div className="bg-white border-4 border-black shadow-[8px_8px_0px_black] p-6">
          <h3 className="text-2xl font-black mb-6">HOW TO INITIATE A RETURN</h3>
          
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: 'Contact Us',
                desc: 'WhatsApp us at +91 70540 83677 or email support@owlfithood.com within 7 days of delivery'
              },
              {
                step: 2,
                title: 'Share Details',
                desc: 'Provide your order number, reason for return, and photos if product is damaged'
              },
              {
                step: 3,
                title: 'Get Approval',
                desc: 'We\'ll review and approve your request within 24 hours'
              },
              {
                step: 4,
                title: 'Schedule Pickup',
                desc: 'Our courier partner will pick up the product from your address'
              },
              {
                step: 5,
                title: 'Get Refund',
                desc: 'Refund will be processed within 5-7 business days after receiving the product'
              }
            ].map((item) => (
              <div key={item.step} className="flex gap-4">
                <div className="w-12 h-12 bg-black text-white flex items-center justify-center font-black text-xl flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h4 className="font-black text-lg">{item.title}</h4>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Exchange Policy */}
        <div className="bg-blue-50 border-4 border-blue-400 p-6">
          <h3 className="text-2xl font-black mb-4">SIZE EXCHANGE</h3>
          <p className="mb-4">
            Ordered the wrong size? No worries! We offer <strong>free size exchanges</strong> (subject to availability).
          </p>
          <ul className="space-y-2 text-sm">
            <li>• Exchange must be requested within 7 days of delivery</li>
            <li>• Product must be unused with original tags</li>
            <li>• If requested size is unavailable, we'll process a full refund</li>
            <li>• Exchange delivery takes 5-7 business days</li>
          </ul>
        </div>

        {/* Refund Details */}
        <div className="bg-white border-4 border-black shadow-[8px_8px_0px_black] p-6">
          <h3 className="text-2xl font-black mb-4">REFUND DETAILS</h3>
          
          <table className="w-full">
            <thead>
              <tr className="bg-black text-white">
                <th className="p-3 text-left">Payment Method</th>
                <th className="p-3 text-left">Refund Timeline</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-200">
                <td className="p-3 font-bold">UPI / Net Banking</td>
                <td className="p-3">3-5 business days</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="p-3 font-bold">Credit / Debit Card</td>
                <td className="p-3">5-7 business days</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="p-3 font-bold">Cash on Delivery</td>
                <td className="p-3">5-7 business days (to bank account)</td>
              </tr>
              <tr>
                <td className="p-3 font-bold">Wallet</td>
                <td className="p-3">Instant - 24 hours</td>
              </tr>
            </tbody>
          </table>
          
          <p className="mt-4 text-sm text-gray-600">
            * Shipping charges are non-refundable for change-of-mind returns
          </p>
        </div>

        {/* Contact */}
        <div className="bg-green-500 text-white border-4 border-black shadow-[8px_8px_0px_black] p-8 text-center">
          <h3 className="text-2xl font-black mb-4">NEED HELP?</h3>
          <p className="mb-6">Our support team is here to help you 7 days a week</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://wa.me/917054083677?text=Hi!%20I%20need%20help%20with%20a%20return"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 font-black border-2 border-black"
            >
              <MessageCircle size={20} />
              WhatsApp Us
            </a>
            <a
              href="mailto:support@owlfithood.com"
              className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 font-black border-2 border-white"
            >
              Email Support
            </a>
          </div>
        </div>

        {/* Last Updated */}
        <p className="text-center text-sm text-gray-500">
          Last updated: January 2025 | Policy subject to change without notice
        </p>
      </div>
    </div>
  );
};

export default ReturnPolicy;
